import { jsx } from "react/jsx-runtime";
import { useEffect } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { CasinoFixedCategories, clearLastPlayedGamesList, fetchLastPlayedGames } from "@nxplatform/casino/casino-category.slice";
import { PrivateRoute } from "@nxplatform/helpers/common/private-route";
import { getIsMobile } from "@nxplatform/helpers/helpers-pure";
import { allowedLinks, generateLinkPart, OuterMenuSport } from "@nxplatform/platform-settings/locale-setup.routes";
import { selectCustomerTokenEntities } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { useCasinoGetMyFavoritesGamesHook } from "../../hooks/casino-get-my-favorite-games.hook.js";
import { useCasinoGetRecentlyGamesHook } from "../../hooks/casino-get-recently-games.hook.js";
import { mergeRoutes, filterRoutes } from "./helpers.js";
import { selectPlatformSettingsLiveCasinoEnabled, selectPlatformSettingsCasinoEnabled, selectCategoryRecentlyPlayedEnabled, selectShowCasinoDemoGames } from "@nxplatform/platform-settings/platform-settings.selectors";
const useCreateCasinoRoutes = (pages, Container, additional) => {
  const casinoSections = [allowedLinks.casino, allowedLinks.liveCasino];
  const {
    category: Category,
    producer: Producer,
    game: Game,
    demoGame: DemoGame,
    page: Page,
    customlobby = false
  } = pages || {};
  const {
    showCasinoDemoGames,
    categoryRecentlyPlayedEnabled,
    auth,
    casinoEnabled,
    liveCasinoEnabled
  } = useSelector((state) => ({
    showCasinoDemoGames: selectShowCasinoDemoGames(state),
    categoryRecentlyPlayedEnabled: selectCategoryRecentlyPlayedEnabled(state),
    auth: selectCustomerTokenEntities(state),
    casinoEnabled: selectPlatformSettingsCasinoEnabled(state),
    liveCasinoEnabled: selectPlatformSettingsLiveCasinoEnabled(state)
  }), shallowEqual);
  const isMobile = getIsMobile();
  const dispatch = useDispatch();
  const getGamesCasinoLastPlayed = (filters) => {
    dispatch(fetchLastPlayedGames(filters));
  };
  const clearLastPlayedList = () => {
    dispatch(clearLastPlayedGamesList());
  };
  useCasinoGetRecentlyGamesHook({
    auth: !!auth,
    categoryRecentlyPlayedEnabled,
    getGamesCasinoLastPlayed
  });
  useCasinoGetMyFavoritesGamesHook({
    auth: !!auth
  });
  useEffect(() => {
    if (categoryRecentlyPlayedEnabled && !auth) {
      return () => {
        clearLastPlayedList();
      };
    }
  }, [auth]);
  const getCategoryRoutes = (isLive) => {
    return [
      {
        path: isLive ? generateLinkPart(OuterMenuSport.liveCasinoProducer, [allowedLinks.liveCasino]) : generateLinkPart(OuterMenuSport.casinoProducer, [allowedLinks.casino]),
        element: isMobile && pages ? /* @__PURE__ */ jsx(Producer, {}) : /* @__PURE__ */ jsx(Page, {})
      },
      {
        path: isLive ? generateLinkPart(OuterMenuSport.liveCasinoCategory, [allowedLinks.liveCasino]) : generateLinkPart(OuterMenuSport.casinoCategory, [allowedLinks.casino]),
        element: isMobile && pages ? /* @__PURE__ */ jsx(Category, {}) : /* @__PURE__ */ jsx(Page, {})
      },
      {
        path: isLive ? generateLinkPart(OuterMenuSport.liveCasinoCategoryProducer, [allowedLinks.liveCasino]) : generateLinkPart(OuterMenuSport.casinoCategoryProducer, [allowedLinks.casino]),
        element: isMobile && pages ? /* @__PURE__ */ jsx(Producer, {}) : /* @__PURE__ */ jsx(Page, {})
      }
    ];
  };
  if (!pages) return [];
  const routes = casinoSections.filter(
    (section) => casinoEnabled && (section === allowedLinks.liveCasino ? liveCasinoEnabled : true)
  ).map((section) => [
    {
      element: /* @__PURE__ */ jsx(Container, {}),
      path: `${section}/*`,
      children: mergeRoutes([
        {
          path: generateLinkPart(OuterMenuSport.casinoDemoGame, [allowedLinks.casino]),
          element: showCasinoDemoGames ? /* @__PURE__ */ jsx(DemoGame, { isDemo: true }) : /* @__PURE__ */ jsx(Navigate, { to: allowedLinks.casino, replace: true })
        },
        {
          path: generateLinkPart(OuterMenuSport.casinoGame, [allowedLinks.casino]),
          element: /* @__PURE__ */ jsx(PrivateRoute, { auth, redirectTo: "/", children: /* @__PURE__ */ jsx(Game, {}) })
        },
        {
          path: generateLinkPart(OuterMenuSport.liveCasinoGame, [allowedLinks.liveCasino]),
          element: /* @__PURE__ */ jsx(PrivateRoute, { auth, redirectTo: "/", children: /* @__PURE__ */ jsx(Game, {}) })
        },
        customlobby && section === allowedLinks.casino && {
          index: true,
          element: /* @__PURE__ */ jsx(Navigate, { to: `${allowedLinks.casino}/${CasinoFixedCategories.lobby}`, replace: true })
        },
        ...getCategoryRoutes(section === allowedLinks.liveCasino),
        {
          path: "*",
          element: /* @__PURE__ */ jsx(Page, {})
        }
      ], additional).filter(filterRoutes)
    }
  ]).flat();
  return routes;
};
export {
  useCreateCasinoRoutes
};
